// src/contexts/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import { AVAILABLE_LANGUAGES, LanguageCode } from '../config/i18n';
import { normalizeLanguageCode } from '../config/normalizeLanguageCode';
import { applyMomentLocaleFromI18nLanguage } from '../utils/momentI18nLocale';
import { languageService } from '../services/languageService';
import { markLanguageChoice } from '../utils/marketLanguage';

interface LanguageContextType {
    currentLanguage: LanguageCode;
    changeLanguage: (lang: LanguageCode) => Promise<void>;
    availableLanguages: typeof AVAILABLE_LANGUAGES;
    isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
        () => languageService.getLocalLanguage() || (normalizeLanguageCode(i18n.language) ?? 'fr')
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const dayjsLocaleMap: Record<LanguageCode, string> = {
            en: 'en',
            fr: 'fr',
            es: 'es',
            de: 'de',
            it: 'it',
            'nl-BE': 'nl',
            'nl-NL': 'nl',
        };

        dayjs.locale(dayjsLocaleMap[currentLanguage] || 'fr');
        applyMomentLocaleFromI18nLanguage(currentLanguage);
    }, [currentLanguage]);

    useEffect(() => {
        const handleLanguageChanged = (lng: string) => {
            setCurrentLanguage(lng as LanguageCode);
        };

        i18n.on('languageChanged', handleLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n]);

    // Utilisateur connecté : serveur si dispo, sinon local (et sync serveur si besoin)
    useEffect(() => {
        if (!localStorage.getItem('token')) return;

        let cancelled = false;
        (async () => {
            const lang = await languageService.initializeLanguage();
            const current = normalizeLanguageCode(i18n.language);
            if (!cancelled && lang && lang !== current) {
                await i18n.changeLanguage(lang);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [i18n]);

    const changeLanguage = async (lang: LanguageCode) => {
        setIsLoading(true);
        try {
            markLanguageChoice('explicit');
            languageService.setLocalLanguage(lang);
            await i18n.changeLanguage(lang);
            await languageService.syncToBackend(lang);
        } catch (error) {
            console.error('Error changing language:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const value: LanguageContextType = {
        currentLanguage,
        changeLanguage,
        availableLanguages: AVAILABLE_LANGUAGES,
        isLoading,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
